import React, { useEffect, useRef } from 'react';
import { Box, Heading, useAnalytics } from '@medely/ui-kit';
import { ContentLoader } from '../../../components';
import { NoPositionsAndSpecialties } from './components/NoPositionsAndSpecialties';
import { PositionProps } from './interface';
import { PositionAndSpecialtyItem } from './components/PositionAndSpecialtyItem';
import { formattedSpecialties } from '../utils';
import { SpecialtiesView } from './SpecialtiesView';
import { HasNoSpecialtyInfo } from './components/HasNoSpecialtyView';
import { useSpecialtyTracking } from '@mp/hooks';
import { useCurrentUserForPositionsWithSpecialties, usePositionsWithSpecialties } from './graphql';
import { useSpecialtiesMutations } from './mutations';

type PositionsAndSpecialtiesViewProps = {
  showTitle?: boolean;
  onPageChange?: (params: boolean) => void;
  deepLinkParams: {
    os: string | null;
    utm_source?: string | null;
    utm_campaign?: string | null;
    utm_term?: string | null;
  };
};

export const PositionsAndSpecialtiesView = ({
  showTitle = false,
  onPageChange,
  deepLinkParams,
}: PositionsAndSpecialtiesViewProps) => {
  const [selectedPosition, setSelectedPosition] = React.useState<undefined | number>();
  const analytics = useAnalytics();
  const { trackSpecialtiesAndPositionsViewed } = useSpecialtyTracking(analytics);

  const { currentUser, loadingUser } = useCurrentUserForPositionsWithSpecialties();
  const hasPosted = useRef(false);
  const { trackViewedSpecialties } = useSpecialtiesMutations();
  const professionalPositions = currentUser?.professional?.positions ?? [];
  const professionalPositionIds = professionalPositions.map(({ id }) => id);
  const professionalSpecialties = currentUser?.professional?.specialties;

  const { allPositionsWithSpecialties, loadingPositions } =
    usePositionsWithSpecialties(professionalPositionIds);

  const formattedPositions: PositionProps[] = professionalPositions.map((position) => ({
    header: position.display_name ?? '',
    description: formattedSpecialties({
      positionId: position.id,
      professionalSpecialties,
      allPositionsWithSpecialties,
    }),
    id: position.id,
  }));

  useEffect(() => {
    trackSpecialtiesAndPositionsViewed(deepLinkParams);
  }, []);

  useEffect(() => {
    if (hasPosted.current) {
      return;
    }
    hasPosted.current = true;
    trackViewedSpecialties();
  }, [trackViewedSpecialties]);

  const handleUpdatePositionClick = (positionId: number) => {
    setSelectedPosition(positionId);
  };

  // We call this every render to ensuer the header is shown/hidden accurately
  // Normally we'd place this logic in the handlers themselves, however the handler
  // is prop drilled far enough that it adds unexpected complexity, in which this
  // solution works better then the handlers.
  // TODO(PRO-3729): Look at refactor opportunities
  onPageChange && onPageChange(true);
  if (!!selectedPosition) {
    onPageChange && onPageChange(false);
    const positionWithSpecialtiesInfo = allPositionsWithSpecialties?.find(
      (position) => position.id === selectedPosition,
    );
    const hasSpecialties = !!positionWithSpecialtiesInfo?.specialties;

    return (
      <ContentLoader loading={loadingUser || loadingPositions}>
        {hasSpecialties ? (
          <SpecialtiesView
            positionWithSpecialtiesInfo={positionWithSpecialtiesInfo}
            professionalSpecialties={professionalSpecialties}
            onBackClick={setSelectedPosition}
          />
        ) : (
          <HasNoSpecialtyInfo
            handleBackClick={setSelectedPosition}
            positionName={positionWithSpecialtiesInfo?.display_name || ''}
          />
        )}
      </ContentLoader>
    );
  }

  return (
    <ContentLoader loading={loadingUser || loadingPositions}>
      <>
        {showTitle && (
          <Heading size="m" color="text.primary" testId="positions-and-specialties-title">
            Positions and specialties
          </Heading>
        )}
        {!!formattedPositions.length ? (
          <Box testId="positions-and-specialties-list">
            {formattedPositions.map(({ header, description, id }) => (
              <Box key={`position-${id}`} onClick={() => handleUpdatePositionClick(id)}>
                <PositionAndSpecialtyItem header={header} description={description} id={id} />
              </Box>
            ))}
          </Box>
        ) : (
          <NoPositionsAndSpecialties />
        )}
      </>
    </ContentLoader>
  );
};
